class PredictiveSearch extends HTMLElement {
  constructor() {
    super();

    this.selectors = {
      searchQuery: "[data-query]",
      searchMessage: "[data-message]",
      input: "[data-search-input]",
      submit: 'button[type="submit"]',
      loading: "[data-spinner]",
      clear: "[data-clear-search]",
      popularSearchItems: ["[data-ps-item]"],
      moreResultIcon: "[data-more-result-icon]",
    };

    this.container = document.querySelector("[data-search-popup]");

    this.domNodes = queryDomNodes(this.selectors, this.container);

    this.cachedResults = {};
    this.transitionDuration = 300;
    this.input = this.querySelector('input[type="search"]');
    this.predictiveSearchResults = this.querySelector("[data-predictive-search]");
    this.searchPopupSearults = this.querySelector(".m-search-popup__result");
    this.popularSearches = this.querySelector("[data-popular-searches]");
    this.searchCount = this.querySelector("[data-search-count]");

    this.setupEventListeners();
  }

  setPopularSearchesLink = () => {
    const { popularSearchItems } = this.domNodes;
    if (!popularSearchItems) return;
    popularSearchItems.forEach((itm) => (itm.href = createSearchLink(itm.dataset.psQuery)));
  };

  setupEventListeners() {
    // Guard (2026-07-27): same custom-element upgrade race as MHeader
    // (2026-07-16) — the search markup may not be parsed when the element
    // upgrades, so form/input/clear are null and the unguarded
    // addEventListener threw in the constructor, killing predictive search
    // for the whole session ("reading 'addEventListener'", desktop).
    // Each listener binds independently; retry once children exist.
    this.bound = this.bound || {};
    const { clear } = this.domNodes;
    const form = this.querySelector("form.m-search-form");

    if (form && !this.bound.form) {
      this.bound.form = true;
      form.addEventListener("submit", this.onFormSubmit.bind(this));
    }

    if (this.input && !this.bound.input) {
      this.bound.input = true;
      this.input.addEventListener(
        "input",
        debounce((event) => {
          this.onChange(event);
        }, 300).bind(this)
      );
    }

    if (clear && !this.bound.clear) {
      this.bound.clear = true;
      clear.addEventListener("click", this.onClearSearch.bind(this));
    }

    if (!this.bound.pubsub) {
      this.bound.pubsub = true;
      window.MinimogEvents && window.MinimogEvents.subscribe(MinimogTheme.pubSubEvents.closeSearchPopup, () => this.close(true));
    }

    if (form && this.input && clear) return;
    if (this.listenersRetried) return;
    this.listenersRetried = true;
    requestAnimationFrame(() => this.rebindMissingNodes());
  }

  rebindMissingNodes() {
    this.container = this.container || document.querySelector("[data-search-popup]");
    this.domNodes = queryDomNodes(this.selectors, this.container);
    this.input = this.input || this.querySelector('input[type="search"]');
    this.predictiveSearchResults = this.predictiveSearchResults || this.querySelector("[data-predictive-search]");
    this.searchPopupSearults = this.searchPopupSearults || this.querySelector(".m-search-popup__result");
    this.popularSearches = this.popularSearches || this.querySelector("[data-popular-searches]");
    this.searchCount = this.searchCount || this.querySelector("[data-search-count]");
    this.setupEventListeners();
  }

  // Guard: every method below can be reached before the markup is parsed —
  // close() in particular fires from the closeSearchPopup pubsub and from the
  // fetch catch, neither of which depends on the input listener having bound.
  get nodesReady() {
    return !!(this.input && this.predictiveSearchResults && this.searchPopupSearults && this.searchCount);
  }

  getQuery() {
    return this.input ? this.input.value.trim() : "";
  }

  onChange() {
    const searchTerm = this.getQuery();

    if (!searchTerm.length) {
      this.close(true);
      return;
    }

    this.getSearchResults(searchTerm);
  }

  onFormSubmit(event) {
    if (!this.getQuery().length || this.querySelector('[aria-selected="true"] a')) event.preventDefault();
  }

  onClearSearch(event) {
    event.preventDefault();
    if (!this.input) return;
    this.input.value = "";
    this.onChange();
  }

  getSearchResults(searchTerm) {
    const queryKey = searchTerm.replace(" ", "-").toLowerCase();
    this.toggleSpinnerLoading(true);
    this.toggleClearSearch(false);

    const searchByTag = this.dataset.searchByTag === "true";
    const searchByBody = this.dataset.searchByBody === "true";
    const unavailableProductsOption = this.dataset.unavailableProductsOption;

    let searchFields = "title,product_type,vendor,variants.sku,variants.title";

    if (searchByTag) searchFields += ",body";
    if (searchByBody) searchFields += ",tag";

    if (this.cachedResults[queryKey]) {
      this.renderSearchResults(this.cachedResults[queryKey]);
      return;
    }

    let searchURL = `${window.MinimogSettings.routes.predictive_search_url}?q=${encodeURIComponent(
      searchTerm
    )}&resources[type]=product,page,article&resources[options][unavailable_products]=${unavailableProductsOption}&resources[options][fields]=${searchFields}&section_id=predictive-search`;

    fetch(`${searchURL}`)
      .then((response) => {
        if (!response.ok) {
          var error = new Error(response.status);
          this.close();
          throw error;
        }
        return response.text();
      })
      .then((text) => {
        const resultsMarkup = new DOMParser()
          .parseFromString(text, "text/html")
          .querySelector("#shopify-section-predictive-search").innerHTML;
        this.cachedResults[queryKey] = resultsMarkup;
        this.renderSearchResults(resultsMarkup);
      })
      .catch((error) => {
        this.close();
        throw error;
      });
  }

  renderSearchResults(resultsMarkup) {
    if (!this.predictiveSearchResults) return;
    this.predictiveSearchResults.innerHTML = resultsMarkup;
    this.setAttribute("results", true);
    const searchItemsWrapper = this.querySelector("[data-search-items-wrapper]");
    if (searchItemsWrapper && searchItemsWrapper.childElementCount > 0) {
      this.renderSearchQueryAndMessage(true);
    } else {
      this.renderSearchQueryAndMessage(false);
    }

    this.toggleSpinnerLoading(false);
    this.toggleClearSearch(true);
    this.open();
  }

  renderSearchQueryAndMessage(results) {
    const { input, searchQuery, searchMessage, moreResultIcon } = this.domNodes;
    if (!input || !searchQuery || !searchMessage || !moreResultIcon) return;
    const query = input.value;

    const { resultsTitle } = searchMessage.dataset;
    searchQuery.textContent = query;

    this.predictiveSearchResults && this.predictiveSearchResults.classList.remove("m:hidden");
    this.searchPopupSearults && this.searchPopupSearults.classList.remove("m:hidden");

    if (results) {
      searchMessage.textContent = resultsTitle;
      moreResultIcon.classList.add("m:hidden");
    } else {
      searchMessage.textContent = searchMessage.dataset.resultsTitle;
      moreResultIcon.classList.remove("m:hidden");
    }
  }

  toggleSpinnerLoading(show) {
    const { loading, submit } = this.domNodes;
    if (submit) submit.style.visibility = show ? "hidden" : "visible";
    if (loading) loading.style.visibility = show ? "visible" : "hidden";
  }

  toggleClearSearch(show) {
    const { clear } = this.domNodes;
    if (!clear) return;
    clear.style.visibility = show ? "visible" : "hidden";
  }

  open() {
    if (!this.nodesReady) return;
    this.searchPopupSearults.style.setProperty(
      "--search-result-max-height",
      `${this.resultsMaxHeight || this.getResultsMaxHeight()}px`
    );
    this.setAttribute("open", true);
    this.input.setAttribute("aria-expanded", true);
    this.isOpen = true;

    this.popularSearches && this.popularSearches.classList.add("m:hidden");
    this.searchCount.classList.remove("m:hidden");
  }

  close(clearSearchTerm = false) {
    if (!this.nodesReady) return;
    if (clearSearchTerm) {
      this.input.value = "";
      this.removeAttribute("results");
      this.toggleClearSearch(false);
      this.resultsMaxHeight = false;
    }

    this.removeAttribute("open");
    this.predictiveSearchResults.classList.add("m:hidden");
    this.searchPopupSearults.classList.add("m:hidden");
    this.popularSearches && this.popularSearches.classList.remove("m:hidden");
    this.searchCount.classList.add("m:hidden");
  }

  getResultsMaxHeight() {
    this.resultsMaxHeight =
      window.innerHeight - document.querySelector("predictive-search").getBoundingClientRect().bottom;
    return this.resultsMaxHeight;
  }
}

customElements.define("predictive-search", PredictiveSearch);
